import * as React from 'react';
import {HashRouter, Switch, Route} from 'react-router-dom';

import {Routing} from 'nutshell-core/routing';

import {Modal} from 'shells/modal/modal';
import {LeftFrame} from 'shells/modal/step-modal/layout';
import {StepList} from 'shells/modal/step-modal/step-list';

import {useGetSessionUser} from '../../../core/use-get-session-user';

import {appHistory} from '../../../../history';

import {GetEmailBlocklist} from '../email-syncing/get-email-blocklist';
import {getSettingsFromUserEmail} from '../smtp/get-settings-from-user-email';

import {ChooseProvider} from './choose-provider';
import {GettingStarted} from './getting-started';
import {ConfigureSmtp} from './smtp-setup/configure-smtp';
import {SmtpFailure} from './smtp-setup/smtp-failure';
import {SmtpSuccess} from './smtp-setup/smtp-success';
import {Timeout} from './smtp-setup/timeout';
import {SuccessfulSyncableConnection} from './successful-syncable-connection';
import {Blocklist} from './sync-setup/blocklist';
import {Done} from './sync-setup/done';
import {DoneNoSync} from './sync-setup/done-no-sync';
import {InitialSync} from './sync-setup/initial-sync';
import {SyncPreferences} from './sync-setup/sync-preferences';

type Props = {
    isOpen: boolean;
    onClose: () => void;
};

const PATH_STEPS = {
    '/connect-email/get-started': 0,
    '/connect-email/choose-provider': 1,
    '/connect-email/configure-smtp': 1,
    '/connect-email/smtp-failure': 1,
    '/connect-email/smtp-success': 3,
    '/connect-email/smtp-verifying': 1,
    '/connect-email/connection-successful': 2,
    '/connect-email/sync-preferences': 2,
    '/connect-email/initial-sync': 2,
    '/connect-email/blocklist': 2,
    '/connect-email/done': 3,
    '/connect-email/done-no-sync': 3,
} as const;

const STEPS = ['1. Get started', '2. Connect your email', '3. Sync your email', '4. You’re done!'];

export function ConnectEmailModalRoot(props: Props) {
    const {refetch: refetchSessionUser} = useGetSessionUser();

    React.useEffect(() => {
        const handleEmailConnected = () => {
            refetchSessionUser();
        };

        window.addEventListener('email-connected', handleEmailConnected);

        return () => {
            window.removeEventListener('email-connected', handleEmailConnected);
        };
    }, [refetchSessionUser]);

    // Redirect to sending settings if page is refreshed on `smtp-verifying` modal page.
    React.useEffect(() => {
        if (location.hash && location.hash.includes('smtp-verifying')) {
            appHistory.replace('/my-account/email-sending');
        }
    }, []);

    return (
        <Modal
            isOpen={props.isOpen}
            onClose={props.onClose}
            canClickOutsideToClose={false}
            canEscapeToClose={false}
            maxHeight={600}
            noPadding={true}
        >
            <div className='flex full-height'>
                <HashRouter>
                    <LeftFrame title='Set up your email'>
                        <Route
                            render={({location}) => {
                                // @ts-expect-error
                                const activeStep = PATH_STEPS[location.pathname];

                                const skips = location.pathname.includes('smtp')
                                    ? [{index: 2, description: 'Not possible with SMTP'}]
                                    : undefined;

                                return (
                                    <StepList
                                        activeStepIndex={activeStep}
                                        skips={skips}
                                        steps={STEPS}
                                    />
                                );
                            }}
                        />
                    </LeftFrame>
                    <Switch>
                        <Route
                            path='/connect-email/get-started'
                            render={({location}) => (
                                <GettingStarted
                                    currentUrl={location.pathname}
                                    onCancel={() => {
                                        props.onClose();
                                    }}
                                />
                            )}
                        />
                        <Route
                            path='/connect-email/choose-provider'
                            render={({history, location}) => (
                                <ChooseProvider
                                    currentUrl={location.pathname}
                                    history={history}
                                    prevPage='/connect-email/get-started'
                                    onCancel={() => {
                                        props.onClose();
                                    }}
                                />
                            )}
                        />
                        <Route
                            path='/connect-email/configure-smtp'
                            render={({history, location}) => (
                                <ConfigureSmtp
                                    currentUrl={location.pathname}
                                    goBack={() => {
                                        history.push('/connect-email/choose-provider');
                                    }}
                                    onSuccess={() => {
                                        history.push('/connect-email/smtp-success');
                                    }}
                                    onVerificationTimeout={() => {
                                        history.push('/connect-email/smtp-verifying');
                                    }}
                                    onVerificationFailure={(userEmail) => {
                                        const {hostname, port, ssl, username, password} =
                                            getSettingsFromUserEmail(userEmail);

                                        const params = {
                                            h: hostname,
                                            p: port,
                                            pt: ssl,
                                            un: username,
                                            id: userEmail.id,
                                            pw: Boolean(password),
                                        } as const;

                                        history.push(
                                            `/connect-email/smtp-failure?${Routing.param(params)}`
                                        );
                                    }}
                                />
                            )}
                        />
                        <Route
                            path='/connect-email/smtp-failure'
                            render={({history, location}) => {
                                const params = Routing.deparam(location.search);
                                const {h, p, pt, un, id, pw} = params;

                                return (
                                    <SmtpFailure
                                        currentUrl={location.pathname}
                                        onCancel={props.onClose}
                                        onSuccess={() => {
                                            history.push('/connect-email/smtp-success');
                                        }}
                                        onVerificationTimeout={() => {
                                            history.push('/connect-email/smtp-verifying');
                                        }}
                                        userEmailId={id}
                                        hostname={h}
                                        port={p}
                                        password={pw ? '••••••••' : ''}
                                        portType={pt}
                                        username={un}
                                    />
                                );
                            }}
                        />
                        <Route
                            path='/connect-email/smtp-success'
                            render={({location}) => (
                                <SmtpSuccess
                                    onClose={props.onClose}
                                    currentUrl={location.pathname}
                                />
                            )}
                        />
                        <Route
                            path='/connect-email/smtp-verifying'
                            render={({location}) => (
                                <Timeout onClose={props.onClose} currentUrl={location.pathname} />
                            )}
                        />
                        <Route
                            path='/connect-email/connection-successful'
                            render={({location}) => (
                                <SuccessfulSyncableConnection
                                    currentUrl={location.pathname}
                                    nextPage={`/connect-email/sync-preferences${location.search}`}
                                    doneNoSyncPage='/connect-email/done-no-sync'
                                    onClose={props.onClose}
                                />
                            )}
                        />
                        <Route
                            path='/connect-email/sync-preferences'
                            render={({history, location}) => {
                                return (
                                    <SyncPreferences
                                        currentUrl={location.pathname}
                                        goBack={history.goBack}
                                        nextPage={`/connect-email/initial-sync${location.search}`}
                                    />
                                );
                            }}
                        />
                        <Route
                            path='/connect-email/initial-sync'
                            render={({history, location}) => (
                                <InitialSync
                                    currentUrl={location.pathname}
                                    goBack={history.goBack}
                                    nextPage={`/connect-email/blocklist${location.search}`}
                                />
                            )}
                        />
                        <Route
                            path='/connect-email/blocklist'
                            render={({history, location}) => {
                                const params = Routing.deparam(location.search);

                                return (
                                    <GetEmailBlocklist>
                                        {({isLoading, blocklistedEmails, setBlocklist}) => {
                                            return (
                                                <Blocklist
                                                    currentUrl={location.pathname}
                                                    goBack={history.goBack}
                                                    onSyncSuccessful={() =>
                                                        history.push(
                                                            `/connect-email/done${location.search}`
                                                        )
                                                    }
                                                    blocklistedEmails={blocklistedEmails}
                                                    isBlocklistLoading={isLoading}
                                                    setBlocklist={setBlocklist}
                                                    syncOld={params.o === '1'}
                                                    createPeople={params.c === '1'}
                                                    // @ts-expect-error
                                                    provider={providerMap[params.p]}
                                                />
                                            );
                                        }}
                                    </GetEmailBlocklist>
                                );
                            }}
                        />
                        <Route
                            path='/connect-email/done'
                            render={({location}) => {
                                const params = Routing.deparam(location.search);
                                const createPeople = params.c === '1';

                                return (
                                    <Done
                                        currentUrl={location.pathname}
                                        onGoToSettings={() =>
                                            appHistory.push('/my-account/email-sending')
                                        }
                                        onGoToContacts={() => {
                                            props.onClose();
                                            appHistory.push('/people');
                                        }}
                                        createPeopleOnInitialSync={createPeople}
                                    />
                                );
                            }}
                        />
                        {NutClientConfig.hasImprovedEmailCalendarSyncFF && (
                            <Route
                                path='/connect-email/done-no-sync'
                                render={({location}) => (
                                    <DoneNoSync
                                        currentUrl={location.pathname}
                                        onGoToSettings={() =>
                                            appHistory.push('/my-account/email-sending')
                                        }
                                    />
                                )}
                            />
                        )}
                    </Switch>
                </HashRouter>
            </div>
        </Modal>
    );
}

const providerMap = {
    g: 'GOOGLE',
    o: 'OFFICE365',
} as const;
